import Vue from 'vue'
import VueRouter, { RawLocation, Route, RouteConfig, RouterOptions } from 'vue-router'
import { camelCase, lowerFirst, upperFirst } from 'lodash'
import { BaseError, BaseHook, BaseUserModule } from '@/types'
import { loadModuleConfigs } from '@/utils/config-util'
import { createHooks } from '@/utils/hook-util'
import { sleep } from '@/utils/timer-util'

enum RouteType {
  HOME = 'home',
  NOT_FOUND = 'notFound',
  DYNAMIC = 'dynamic'
}

interface RouteData {
  moduleName: string
  controllerName: string
  entityName: string
  externalUrl: string
  routePath: string
  requestSubpath: string
  accessId: string
}

const routePathHooks = createHooks<string>()
const accessIdHooks = createHooks<string | undefined>()

const push = VueRouter.prototype.push

VueRouter.prototype.push = async function (location: RawLocation): Promise<Route> {
  try {
    return await push.call<VueRouter, [RawLocation], Promise<Route>>(this, location)
  }
  catch (error: any) {
    const { isNavigationFailure, NavigationFailureType } = VueRouter

    if (!isNavigationFailure(error, NavigationFailureType.duplicated)) throw error

    return this.currentRoute
  }
}

export default class BaseVueRouter extends VueRouter {
  private routes: RouteConfig[]

  constructor(routerOptions: RouterOptions = {}) {
    routerOptions.mode = 'hash'
    routerOptions.base = process.env.BASE_URL

    super(routerOptions)

    this.routes = []

    this.afterEach((toRoute) => {
      routePathHooks.execute(toRoute.fullPath)
      accessIdHooks.execute(toRoute.meta?.accessId)
    })
  }

  setDefaultModuleNames(defaultModuleNames: string[]): void {
    this.loadModuleRoutes(defaultModuleNames)
  }

  setUserModules(userModules: BaseUserModule[]): void {
    this.loadModuleRoutes(userModules.map(({ moduleName }) => moduleName))

    this.generateDynamicRoutes(userModules)
  }

  setUserModule(userModule: BaseUserModule): void {
    const homeRoute: RouteConfig = {
      path: '/',
      name: 'app.home',
      redirect: {
        path: userModule.routePath
      }
    }

    this.mergeRoutes([homeRoute], RouteType.HOME)
  }

  async setRoutePath(routePath: string): Promise<void> {
    if (routePath !== this.currentRoute.fullPath) await this.pushRouteLocation(routePath)
    if (routePath !== location.hash.replace('#', '')) location.hash = `#${routePath}`
  }

  setEnabledRouteNotFound(enabledRouteNotFound: boolean): void {
    const notFoundRoute: RouteConfig = {
      path: '*',
      component: () => import('@/modules/app/views/RouteNotFound.vue'),
      props: () => {
        if (!enabledRouteNotFound) this.pushRouteUnauthorized()
      }
    }

    this.mergeRoutes([notFoundRoute], RouteType.NOT_FOUND)
  }

  async pushRouteUnauthorized(unauthorizedError?: BaseError): Promise<void> {
    if (unauthorizedError) await sleep(500)

    if (this.currentRoute.name !== 'auth.login') {
      return this.pushRouteLocation({
        name: 'auth.login',
        query: {
          nextRoute: this.currentRoute.fullPath
        }
      })
    }
  }

  registerRoutePathHook(routePathHook: BaseHook<string>): void {
    routePathHooks.register(routePathHook)
  }

  registerAccessIdHook(accessIdHook: BaseHook<string | undefined>): void {
    accessIdHooks.register(accessIdHook)
  }

  private loadModuleRoutes(moduleNames: string[]): void {
    const moduleRoutes: RouteConfig[] = []

    loadModuleConfigs<RouteConfig[]>('/routes', moduleNames, ({ moduleValue }) => {
      moduleRoutes.push(...moduleValue)
    })

    this.mergeRoutes(moduleRoutes)
  }

  private generateDynamicRoutes(userModules: BaseUserModule[]): void {
    const dynamicRoutes: RouteConfig[] = []

    let redirectRouteLocation: RawLocation = { name: 'auth.login' }

    userModules = ([] as BaseUserModule[]).concat(userModules).reverse()
    userModules.forEach((userModule) => {
      const { moduleName, moduleClientUrl, routePath: moduleRoutePath, controllers, accessId } = userModule
      let redirectRoutePath: string | null = null

      if (moduleClientUrl) {
        const externalUrl = moduleClientUrl
        const routePath = moduleRoutePath
        const requestSubpath = ''
        redirectRoutePath = redirectRoutePath ?? `${routePath}${requestSubpath}`

        const dynamicRoute = this.createDynamicRoute({
          moduleName: 'app',
          controllerName: 'openExternal',
          entityName: 'void',
          externalUrl,
          routePath,
          requestSubpath,
          accessId
        })

        dynamicRoutes.push(dynamicRoute)
      }

      controllers.forEach((controller) => {
        const { controllerName, routePath: controllerRoutePath, entityName, requestDefaultSubpath, requests } = controller
        const externalUrl = ''
        const routePath = controllerRoutePath ?? ''
        redirectRoutePath = redirectRoutePath ?? `${routePath}${requestDefaultSubpath}`

        requests.forEach((request) => {
          const requestSubpath = request.subpath

          const dynamicRoute = this.createDynamicRoute({
            moduleName,
            controllerName,
            entityName,
            externalUrl,
            routePath,
            requestSubpath,
            accessId
          })

          dynamicRoutes.push(dynamicRoute)
        })
      })

      if (redirectRoutePath) redirectRouteLocation = { path: redirectRoutePath }

      dynamicRoutes.push({
        path: moduleRoutePath,
        redirect: redirectRouteLocation
      })
    })

    this.mergeRoutes(dynamicRoutes, RouteType.DYNAMIC)
  }

  private createDynamicRoute(routeData: RouteData): RouteConfig {
    const { moduleName, controllerName, entityName, externalUrl, routePath, requestSubpath, accessId } = routeData
    const viewNameSuffix = upperFirst(camelCase(requestSubpath))
    const viewName = `${upperFirst(controllerName)}${viewNameSuffix}`
    const fallbackViewName = (entityName !== 'void') ? `Crud${viewNameSuffix}` : viewNameSuffix
    const name = `${accessId}.${moduleName}.${lowerFirst(viewName)}`
    const path = `${routePath}${requestSubpath}`
    const viewPath = `@/modules/${moduleName}/views/${viewName}.vue`

    return {
      name,
      path,
      component: async () => {
        try {
          return await import(`@/modules/${moduleName}/views/${viewName}.vue`)
        }
        catch (error: any) {
          try {
            return await import(`@/modules/app/views/${fallbackViewName}.vue`)
          }
          catch (fallbackError: any) {
            return await import('@/modules/app/views/ViewNotImplemented.vue')
          }
        }
      },
      props: () => {
        return {
          moduleName,
          controllerName,
          entityName,
          externalUrl,
          requestSubpath,
          viewPath
        }
      },
      meta: {
        accessId
      }
    }
  }

  private mergeRoutes(routes: RouteConfig[], routeType?: RouteType): void {
    if (routeType) {
      routes.forEach((route) => {
        route.meta = route.meta ?? {}
        route.meta.type = routeType
      })
    }

    routes = this.routes
      .filter((route) => !(routeType && (route.meta?.type === routeType)))
      .filter((route) => !routes.some((someRoute) => someRoute.path === route.path))
      .concat(routes)
      .sort((sourceRoute, targetRoute) => {
        const sourceRoutePath = sourceRoute.path
        const targetRoutePath = targetRoute.path

        return (sourceRoutePath > targetRoutePath) ? -1 : ((sourceRoutePath < targetRoutePath) ? 1 : 0)
      })

    // @ts-ignore
    this.matcher = new VueRouter().matcher
    // @ts-ignore
    routes.forEach((route) => this.matcher.addRoute(route))

    this.options.routes = routes
    this.routes = routes

    // @ts-ignore
    this.history.transitionTo(this.history.getCurrentLocation())
  }

  private async pushRouteLocation(routeLocation: RawLocation): Promise<void> {
    try {
      await this.push(routeLocation)
    }
    catch (error: any) {
      // eslint-disable-next-line no-console
      console.error('Error during route location push: ', error)
    }
  }
}

Vue.use(BaseVueRouter)

export const baseVueRouter = new BaseVueRouter()